import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages } from 'react-intl';
import InfiniteScroll from 'react-infinite-scroller';
import { parseISO } from 'date-fns';
import _isEmpty from 'lodash/isEmpty';
import LoadingDots from 'components/LoadingDots';
import Message from 'components/Message';
import { reviewPropTypes } from 'constants/propTypes';
import RatingsListItem from './RatingsListItem';

import './RatingsList.scss';

const messages = defineMessages({
  weekHeader: {
    id: 'ratings.list.week-header',
    defaultMessage: 'Week of {date, date, short}',
  },
  empty: {
    id: 'ratings.list.empty',
    defaultMessage: 'No reviews added yet',
  },
  emptyWithFilters: {
    id: 'ratings.list.empty-with-filters',
    defaultMessage: 'Reviews for specified filters do not exist',
  },
});

const RatingsListLoader = () => (
  <div className="ratings-list__loader">
    <LoadingDots />
  </div>
);

const RatingsList = ({
  groupedReviews,
  hasMore,
  isLoading,
  isMobile,
  fetchReviewsData,
  reportReviewAsAbuse,
  deleteStudioReply,
  hasActiveFilters,
  shouldRefreshRatingsList,
  isVenueFoodAndBeverage,
}) => {
  if (isLoading && shouldRefreshRatingsList)
    return (
      <div className="ratings-list ratings-list--empty">
        <LoadingDots />
      </div>
    );

  if (_isEmpty(groupedReviews))
    return (
      <Message
        as="div"
        className="ratings-list ratings-list--empty"
        message={hasActiveFilters ? messages.emptyWithFilters : messages.empty}
      />
    );

  const renderWeekHeader = timestamp => (
    <div className="ratings-list__week-header" key={timestamp}>
      <Message message={messages.weekHeader} values={{ date: parseISO(timestamp) }} />
    </div>
  );

  const renderReviews = reviews =>
    reviews.map(review => (
      <RatingsListItem
        key={review.id}
        review={review}
        reportReviewAsAbuse={reportReviewAsAbuse}
        deleteStudioReply={deleteStudioReply}
        isMobile={isMobile}
        isVenueFoodAndBeverage={isVenueFoodAndBeverage}
      />
    ));

  const renderReviewsGroup = ([timestamp, reviews]) => (
    <div key={timestamp}>
      {renderWeekHeader(timestamp)}
      {renderReviews(reviews)}
    </div>
  );

  return (
    <InfiniteScroll
      loadMore={!isLoading && hasMore ? fetchReviewsData : () => {}}
      hasMore={hasMore}
      loader={<RatingsListLoader />}
      initialLoad={false}
      threshold={1000}>
      <div className="ratings-list">{Object.entries(groupedReviews).map(renderReviewsGroup)}</div>
    </InfiniteScroll>
  );
};

RatingsList.propTypes = {
  groupedReviews: PropTypes.shape({
    timestamp: PropTypes.string,
    reviews: PropTypes.arrayOf(PropTypes.shape(reviewPropTypes)),
  }),
  hasMore: PropTypes.bool,
  isLoading: PropTypes.bool,
  isMobile: PropTypes.bool,
  fetchReviewsData: PropTypes.func,
  reportReviewAsAbuse: PropTypes.func,
  deleteStudioReply: PropTypes.func,
  hasActiveFilters: PropTypes.bool,
  shouldRefreshRatingsList: PropTypes.bool,
  isVenueFoodAndBeverage: PropTypes.bool,
};

export default RatingsList;
